import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PredicateBuilder } from './PredicateBuilder';
import { CREATE_PRICE_RULE_MUTATION, PRICE_RULE_SUBJECTS_LOOKUP, UPDATE_PRICE_RULE_MUTATION } from './pricing.gql';

import { SEARCH_SELLABLE_QUERY } from '@/modules/doctor-pos/doctor-pos.gql';
import { SELLABLE_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface LookupsResponse {
  branches: { id: string; name: string }[];
}

interface SearchSellableHit {
  kind: string;
  id: string;
  code: string;
  name: string;
}

interface SearchSellableResponse {
  searchSellable: SearchSellableHit[];
}

interface PriceRuleFormDialogProps {
  rule?: EditablePriceRule | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface EditablePriceRule {
  id: string;
  subject_kind: string;
  subject_id: string;
  subject_name: string | null;
  subject_code: string | null;
  branch_id: string | null;
  priority: number;
  predicate_json: string;
  price_cents: string;
  note: string | null;
}

type PriceRuleSubjectKind = 'product' | 'service' | 'kit';
type PriceRuleSubjectKindGql = 'PRODUCT' | 'SERVICE' | 'KIT';

const PRICE_RULE_SUBJECT_KIND_GQL: Record<PriceRuleSubjectKind, PriceRuleSubjectKindGql> = {
  product: 'PRODUCT',
  service: 'SERVICE',
  kit: 'KIT',
};

function normalizeSubjectKind(kind: string | null | undefined): PriceRuleSubjectKind {
  const normalized = kind?.toLowerCase();
  if (normalized === 'product' || normalized === 'service' || normalized === 'kit') {
    return normalized;
  }
  return 'service';
}

export function PriceRuleFormDialog({
  rule,
  onClose,
  onSaved,
}: PriceRuleFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data: lookups } = useQuery<LookupsResponse>(PRICE_RULE_SUBJECTS_LOOKUP, {
    fetchPolicy: 'cache-first',
  });
  const [createRule, { loading: creating }] = useMutation(CREATE_PRICE_RULE_MUTATION);
  const [updateRule, { loading: updating }] = useMutation(UPDATE_PRICE_RULE_MUTATION);
  const is_editing = Boolean(rule);
  const loading = creating || updating;

  const [subject_kind, setSubjectKind] = useState<PriceRuleSubjectKind>(
    normalizeSubjectKind(rule?.subject_kind),
  );
  const [subject_id, setSubjectId] = useState<string>(rule?.subject_id ?? '');
  const [subject_label, setSubjectLabel] = useState<string>(
    rule?.subject_code && rule.subject_name ? `${rule.subject_code} — ${rule.subject_name}` : '',
  );
  const [branch_id, setBranchId] = useState<string>(rule?.branch_id ?? '');
  const [priority, setPriority] = useState<string>(String(rule?.priority ?? 0));
  const [price_major, setPriceMajor] = useState<string>(
    rule ? centsToMajorString(rule.price_cents) : '',
  );
  const [note, setNote] = useState<string>(rule?.note ?? '');
  const [predicate_json, setPredicateJson] = useState<string>(
    rule?.predicate_json ?? JSON.stringify({ op: 'and', clauses: [] }),
  );
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (): Promise<void> => {
    setError(null);
    if (!subject_id) {
      setError(t('pricing.error_pick_subject'));
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(price_major.trim())) {
      setError(t('pricing.error_invalid_price'));
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(predicate_json);
    } catch {
      setError(t('pricing.error_invalid_predicate'));
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      setError(t('pricing.error_invalid_predicate'));
      return;
    }
    const cents = majorStringToCents(price_major);
    if (cents < 0) {
      setError(t('pricing.error_invalid_price'));
      return;
    }
    const prio = Number.parseInt(priority, 10);
    try {
      const input = {
        priority: Number.isFinite(prio) && prio >= 0 ? prio : 0,
        predicate_json,
        price_cents: String(cents),
        note: note.trim() || null,
      };
      if (rule) {
        await updateRule({
          variables: {
            id: rule.id,
            input,
          },
        });
      } else {
        await createRule({
          variables: {
            input: {
              subject_kind: PRICE_RULE_SUBJECT_KIND_GQL[subject_kind],
              subject_id,
              branch_id: branch_id || null,
              ...input,
            },
          },
        });
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : is_editing ? 'update_failed' : 'create_failed');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="max-h-[92vh] w-full max-w-3xl overflow-auto">
        <CardHeader>
          <CardTitle className="font-display font-bold tracking-tight">{is_editing ? t('pricing.edit_rule') : t('pricing.new_rule')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('pricing.form_hint')}</p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>{t('pricing.subject_kind')}</Label>
            <select
              value={subject_kind}
              onChange={(e) => {
                setSubjectKind(e.target.value as PriceRuleSubjectKind);
                setSubjectId('');
                setSubjectLabel('');
              }}
              disabled={is_editing}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="product">{t('pricing.kind_product')}</option>
              <option value="service">{t('pricing.kind_service')}</option>
              <option value="kit">{t('pricing.kind_kit')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.subject')}</Label>
            <SubjectCombobox
              subject_kind={subject_kind}
              value={subject_id}
              label={subject_label}
              onSelect={(id, label) => {
                setSubjectId(id);
                setSubjectLabel(label);
              }}
              disabled={is_editing}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.branch')}</Label>
            <select
              value={branch_id}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={is_editing}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="">{t('pricing.branch_global')}</option>
              {(lookups?.branches ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.priority')}</Label>
            <Input
              type="number"
              min={0}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.price')}</Label>
            <Input
              inputMode="decimal"
              value={price_major}
              onChange={(e) => setPriceMajor(e.target.value)}
              placeholder="500.00"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.note')}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <PredicateBuilder value={predicate_json} onChange={setPredicateJson} />
          </div>
          {error ? (
            <div className="md:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onSubmit();
            }}
            disabled={loading}
          >
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Debounced search combobox over `SEARCH_SELLABLE_QUERY` (the same query the POS uses),
 * filtered to the chosen `subject_kind`. Replaces the old `<select>` that only ever
 * loaded the first 200 active items — this scales to any catalog size and also resolves
 * correctly for a rule whose subject was later deactivated (the initial `label` comes
 * straight from `rule.subject_name`/`subject_code`, resolved server-side, not from this
 * component's own search).
 */
function SubjectCombobox({
  subject_kind,
  value,
  label,
  onSelect,
  disabled,
}: {
  subject_kind: PriceRuleSubjectKind;
  value: string;
  label: string;
  onSelect: (id: string, label: string) => void;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState(label);
  const [debounced, setDebounced] = useState('');

  useEffect(() => setTerm(label), [label]);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(term.trim()), 200);
    return () => window.clearTimeout(handle);
  }, [term]);

  const { data, loading } = useQuery<SearchSellableResponse>(SEARCH_SELLABLE_QUERY, {
    variables: { input: { term: debounced === '' ? null : debounced, limit: 20 } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const results = (data?.searchSellable ?? []).filter(
    (hit) => (SELLABLE_KIND_FROM_GQL[hit.kind as keyof typeof SELLABLE_KIND_FROM_GQL] ?? hit.kind) === subject_kind,
  );

  return (
    <div className="relative">
      <Input
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          if (value) onSelect('', '');
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder={t('pricing.subject_placeholder')}
        disabled={disabled}
      />
      {open && !disabled ? (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-input bg-card shadow-md">
          {loading ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">{t('common.loading')}</li>
          ) : null}
          {!loading && results.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">{t('doctor_pos.no_results')}</li>
          ) : null}
          {results.map((hit) => (
            <li key={hit.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const next_label = `${hit.code} — ${hit.name}`;
                  onSelect(hit.id, next_label);
                  setTerm(next_label);
                  setOpen(false);
                }}
              >
                <span className="font-mono text-xs text-muted-foreground">{hit.code}</span> {hit.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
